// Shared, honest parsing of supplier prose for the product page.
// Every helper returns only what it can quote back from the feed text —
// nothing here invents a claim.

export const DIMENSIONS_UNAVAILABLE =
  "Dimensions not provided by the supplier for this item.";
export const MATERIALS_UNAVAILABLE =
  "Materials not provided by the supplier for this item.";

const MATERIAL_KEYWORDS = [
  "solid wood", "engineered wood", "reclaimed wood", "mdf", "particleboard", "plywood",
  "metal", "galvanized metal", "steel", "iron", "aluminum", "brass", "chrome",
  "fabric", "leather", "faux leather", "velvet", "linen", "polyester", "cotton",
  "wool", "jute", "sisal", "silk", "coir", "polypropylene",
  "glass", "tempered glass", "marble", "ceramic", "stone", "concrete", "polyresin",
  "rattan", "wicker", "bamboo", "teak", "oak", "walnut", "pine", "mahogany",
  "acacia", "rubber wood", "rubberwood", "foam", "memory foam",
];

// The supplier's attribute block names materials outright: "Primary Material
// Coir", "Material Polypropylene Size …", "Backing Material Vinyl". Straight
// after the word "Material" these are beyond doubt; in prose they are not
// ("crystal clear", "rubber feet", "wood-look", "paper lantern"), so they only
// count in that slot. Without it every coir doormat and a quarter of the
// outdoor rugs read "Materials not provided by the supplier".
const ATTRIBUTE_MATERIALS = [
  "wood", "plastic", "recycled plastic", "polyvinyl chloride", "rubber", "vinyl", "paper", "kraft paper",
  "canvas", "crystal", "lead free crystal", "resin", "cement", "copper", "satin", "felt", "latex", "acrylic",
  "porcelain", "fleece", "sherpa", "faux fur", "rabbit fur", "synthetic",
];
const MATERIAL_ATTRIBUTE_RE = /\bMaterial(?: Details)?:?\s+/g;

const escapeRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// A material word only counts when it names the material, not when it is
// part of a longer material ("leather" inside "faux leather", "glass" inside
// "tempered glass") or a comparison ("feels like leather", "wood-look").
// "cultured marble", "engineered stone", "sintered stone": a composite named
// after what it imitates ("engineered wood" is its own keyword, claimed first).
const SIMILE_BEFORE = /\b(like|look\s+of|feel\s+of|resembl\w*|imitat\w*|mimic\w*|faux|fake|vegan|synthetic|simulated|cultured|engineered|sintered|composite|artificial|man[-\s]?made)\s+(genuine\s+|real\s+)?$/i;
// "stone-colored base", "oak colored drawer pulls", "brass-toned frame",
// "linen colored": the word names a colour. Not "shade": a "linen shade" is a
// lampshade made of linen. Lower case only after the word: in the attribute
// block "Base Material Metal Color Black" opens the next attribute.
const COLOUR_AFTER = /^[-\s]?(?:colou?r(?:ed|s)?|hued?|toned)\b/;
// "Top Color Oak", "color: stone".
const COLOUR_BEFORE = /\bcolou?r:?\s+$/i;
const WOOD_SPECIES = new Set(["oak", "walnut", "teak", "pine", "mahogany", "acacia", "bamboo"]);
const FABRIC_AFTER = /^\s+(velvet|linen|fabric|leather|faux|polyester|cotton|chenille|boucle|bouclé|microfiber|upholster\w*|performance)\b/;
const SIMILE_AFTER = /^[-\s]?(look|like|effect|style|finish|print|pattern|grain|tone|match)\b/i;
// "Floor Compatibility Hardwood, Carpet, Concrete/Stone, Marble/Tile", "on
// hardwood, carpet, linoleum, concrete and marble": the floors a rug pad may
// lie on, not what it is made of. Only an item of that list counts — the word
// follows "compatib…" in the same sentence, or is joined to "carpet" /
// "linoleum" by commas, slashes and "and" — so "over carpet. The steel frame"
// still reads steel.
const FLOORS_BEFORE = /\b(?:compatib\w*\b[^.]{0,100}|(?:carpet|linoleum)\b(?:\s*(?:[,/]|\band\b|\bor\b)\s*(?!(?:the|a|an|its|your)\b)[a-z]*)*\s*)$/i;

export function extractMaterials(desc: string): string[] {
  const text = desc || "";
  const lower = text.toLowerCase();
  // Sort longest first so a compound claims its span before its shorter part.
  const keys = [...MATERIAL_KEYWORDS].sort((x, y) => y.length - x.length);
  const claimed: Array<[number, number]> = [];
  const found = new Map<string, number>(); // material -> first index
  // Words the supplier's own prose uses as a colour: not a material, unless a
  // "Material …" attribute slot (read below) names it.
  const asColour = new Set<string>();
  const covered = (i: number, j: number) => claimed.some(([a, b]) => i >= a && j <= b);

  for (const mat of keys) {
    const re = new RegExp(`\\b${escapeRe(mat)}\\b`, "gi");
    let m: RegExpExecArray | null;
    while ((m = re.exec(lower))) {
      const i = m.index, j = i + mat.length;
      if (covered(i, j)) continue;
      claimed.push([i, j]);
      const before = lower.slice(Math.max(0, i - 24), i);
      const after = lower.slice(j, j + 12);
      if (FLOORS_BEFORE.test(lower.slice(Math.max(0, i - 120), i))) continue;
      if (COLOUR_AFTER.test(text.slice(j, j + 12)) || COLOUR_BEFORE.test(before)) {
        asColour.add(mat);
        continue;
      }
      // "faux leather" is itself a keyword, so only non-compound similes land here.
      if (mat !== "faux leather" && (SIMILE_BEFORE.test(before) || SIMILE_AFTER.test(after))) continue;
      // "a glass of water", "wine glass": drinkware, not what the piece is made of.
      if (mat === "glass" && (/^\s+of\b/.test(after) || /\b(wine|drinking|water|beer|cocktail|champagne|shot)\s+$/.test(before))) continue;
      // "linen cabinet", "linen closet": what it stores, not what it is made of.
      if (mat === "linen" && /^\s+(cabinets?|closets?|towers?|storage)\b/.test(lower.slice(j, j + 20))) continue;
      // "Oak Velvet", "Walnut Linen": a wood name directly before a fabric is a colour.
      if (WOOD_SPECIES.has(mat) && FABRIC_AFTER.test(lower.slice(j, j + 20))) continue;
      if (!found.has(mat)) found.set(mat, i);
    }
  }

  for (const mat of asColour) found.delete(mat);

  // "Solid manufactured wood" is engineered board; only an explicit
  // manufactured/engineered wood phrase makes "Engineered Wood".
  if (/\b(solid\s+)?manufactured\s+wood\b|\bengineered\s+wood\b/.test(lower) && !found.has("engineered wood")) {
    found.set("engineered wood", lower.search(/manufactured\s+wood|engineered\s+wood/));
  }

  // The attribute slots: the longest material name that follows "Material".
  // Read after the prose, so what the description leads with stays first.
  const slotKeys = [...keys, ...ATTRIBUTE_MATERIALS].sort((x, y) => y.length - x.length);
  for (const slot of text.matchAll(MATERIAL_ATTRIBUTE_RE)) {
    const i = (slot.index ?? 0) + slot[0].length;
    const mat = slotKeys.find((k) => lower.startsWith(k, i) && !/[a-z]/.test(lower[i + k.length] ?? ""));
    // "Backing Material Yes", or a value we do not know, adds nothing.
    if (!mat || found.has(mat)) continue;
    // A rug is its pile, not its backing: backing materials are listed last.
    const backing = /backing\s+$/.test(lower.slice(Math.max(0, (slot.index ?? 0) - 10), slot.index ?? 0));
    found.set(mat, backing ? i + lower.length : i);
  }
  // A bare "Wood" from the attribute block adds nothing beside the wood it is.
  if (found.has("wood") && [...found.keys()].some((k) => k !== "wood" && /wood$/.test(k))) found.delete("wood");

  // Once a listing says "faux leather", a bare later "leather" refers back to
  // it. Keep genuine Leather only when the text says so explicitly.
  if (found.has("faux leather") && found.has("leather") &&
      !/\b(genuine|real|top[-\s]grain|full[-\s]grain|bonded|italian)\s+leather\b/.test(lower)) {
    found.delete("leather");
  }

  return [...found.entries()]
    .sort((x, y) => x[1] - y[1])
    .map(([mat]) => (mat === "mdf" ? "MDF" : mat.replace(/\b\w/g, (c) => c.toUpperCase())))
    .slice(0, 6);
}

/* ------------------------------------------------------------------ *
 * Features — a keyword hit is only a feature when it is NOT negated.
 * ------------------------------------------------------------------ */

const NEGATION_WINDOW = 24;
const NEGATION_RE =
  /\b(no|not|none|never|without|excludes?|excluding|lacks?|lacking|minus|omits?|sold\s+separately|does\s+not(?:\s+\w+)?|doesn'?t(?:\s+\w+)?|isn'?t|aren'?t|won'?t|cannot|can'?t)\b[^.]{0,24}$/i;

/**
 * True when the ~24 characters immediately preceding `index` negate the hit,
 * so "does not include storage" or "no drawers" never becomes a bullet.
 */
export function isNegated(text: string, index: number): boolean {
  const before = text.slice(Math.max(0, index - NEGATION_WINDOW), index);
  if (NEGATION_RE.test(before)) return true;
  // "storage sold separately" / "drawer not included" — negation trailing the hit.
  const after = text.slice(index, index + 60);
  if (/^[^.]{0,40}\b(not\s+included|sold\s+separately|excluded)\b/i.test(after)) return true;
  return false;
}

const FEATURE_PATTERNS: Array<{ re: RegExp; label: string }> = [
  { re: /no assembly required/i, label: "No Assembly Required" },
  { re: /easy assembly/i, label: "Easy Assembly" },
  { re: /assembly required/i, label: "Assembly Required" },
  { re: /adjustable height/i, label: "Adjustable Height" },
  { re: /reclining|recliner/i, label: "Reclining" },
  { re: /swivel/i, label: "Swivel" },
  { re: /stackable/i, label: "Stackable" },
  { re: /waterproof/i, label: "Waterproof" },
  { re: /water[-\s]?resistant/i, label: "Water Resistant" },
  { re: /uv[-\s]?resistant/i, label: "UV Resistant" },
  { re: /fire[-\s]?retardant/i, label: "Fire Retardant" },
  { re: /stain[-\s]?resistant/i, label: "Stain Resistant" },
  { re: /usb[^.]{0,12}port/i, label: "USB Port" },
  { re: /power[^.]{0,12}outlet/i, label: "Power Outlet" },
  { re: /storage/i, label: "Storage" },
  { re: /drawers?/i, label: "Drawers" },
  { re: /shelf|shelves/i, label: "Shelving" },
  { re: /foldable|folding/i, label: "Folding" },
  { re: /extendable|expandable/i, label: "Extendable" },
  { re: /cushions?/i, label: "Cushioned" },
  { re: /upholstered/i, label: "Upholstered" },
];

export interface ExtractedFeature {
  label: string;
  /** The exact phrase quoted from the supplier text. */
  quote: string;
}

/**
 * Only emits a feature when the matched phrase can be quoted back from the
 * supplier text and is not preceded (or followed) by a negation.
 */
export function extractFeatures(desc: string): ExtractedFeature[] {
  const text = desc || "";
  const out: ExtractedFeature[] = [];
  for (const { re, label } of FEATURE_PATTERNS) {
    const global = new RegExp(re.source, "gi");
    let m: RegExpExecArray | null;
    while ((m = global.exec(text))) {
      if (isNegated(text, m.index)) continue;
      if (!out.some((f) => f.label === label)) out.push({ label, quote: m[0] });
      break;
    }
  }
  return out.slice(0, 8);
}

/* ------------------------------------------------------------------ *
 * Care — keyed to a material we actually parsed. Never generic.
 * ------------------------------------------------------------------ */

const CARE_BY_MATERIAL: Array<{ match: RegExp; copy: string }> = [
  { match: /^wool$/i, copy: "Wool: vacuum gently without a beater bar, blot spills immediately, and have deeper cleaning done professionally." },
  { match: /^(jute|sisal)$/i, copy: "Natural plant fibre: vacuum regularly, blot spills dry rather than washing, and keep away from standing moisture." },
  { match: /^velvet$/i, copy: "Velvet: brush the pile in one direction, vacuum with an upholstery tool, and blot — never rub — any spill." },
  { match: /^(leather|faux leather)$/i, copy: "Leather: dust with a dry cloth, keep out of direct sun and heat, and use a leather conditioner rather than household cleaners." },
  { match: /^(linen|cotton|polyester|fabric|silk)$/i, copy: "Textile: check the sewn-in care label before washing, and spot-clean with a mild detergent and cool water." },
  { match: /^(solid wood|engineered wood|oak|walnut|pine|teak|mahogany|acacia|rubber ?wood|plywood|mdf|particleboard|bamboo)$/i, copy: "Wood: wipe with a soft cloth slightly dampened with water, dry at once, and use coasters or mats to avoid rings and heat marks." },
  { match: /^(metal|steel|iron|aluminum|brass|chrome)$/i, copy: "Metal: dust with a dry cloth and dry any moisture promptly to avoid marks or corrosion." },
  { match: /^(glass|tempered glass)$/i, copy: "Glass: clean with a soft lint-free cloth and a non-abrasive glass cleaner." },
  { match: /^(marble|stone|concrete|ceramic)$/i, copy: "Stone: wipe with a pH-neutral cleaner, blot acidic spills straight away, and avoid abrasive pads." },
  { match: /^(rattan|wicker)$/i, copy: "Woven fibre: dust with a soft brush and keep out of prolonged damp." },
  { match: /^(foam|memory foam)$/i, copy: "Foam: spot-clean the cover only and let it dry fully before use." },
];

/** Returns care lines only for materials that were parsed from the feed. */
export function careGuidance(materials: string[]): string[] {
  const out: string[] = [];
  for (const mat of materials) {
    const hit = CARE_BY_MATERIAL.find((c) => c.match.test(mat));
    if (hit && !out.includes(hit.copy)) out.push(hit.copy);
  }
  return out.slice(0, 3);
}

/**
 * Assembly time, only when the supplier text states one. Returns the quoted
 * phrase — we never estimate an assembly time ourselves.
 */
export function extractAssemblyTime(desc: string): string | null {
  const text = desc || "";
  const m = text.match(
    /assembl\w*[^.]{0,40}?(\d+\s*(?:-|–|to)?\s*\d*\s*(?:minutes?|mins?|hours?|hrs?))/i
  ) || text.match(
    /(\d+\s*(?:-|–|to)?\s*\d*\s*(?:minutes?|mins?|hours?|hrs?))[^.]{0,30}?assembl\w*/i
  );
  if (!m) return null;
  if (isNegated(text, m.index ?? 0)) return null;
  return m[1].replace(/\s+/g, " ").trim();
}
